import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Menu, 
  X, 
  Home, 
  Package, 
  Camera, 
  FileText, 
  CreditCard, 
  TrendingUp, 
  LogOut,
  Users
} from 'lucide-react';

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logout, currentUser, userRole, canAccessModule } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home, current: location.pathname === '/dashboard' },
    { name: 'Props Rental', href: '/props', icon: Package, current: location.pathname.startsWith('/props'), module: 'props' },
    { name: 'Photography', href: '/photography', icon: Camera, current: location.pathname.startsWith('/photography'), module: 'photography' },
    { name: 'Billing', href: '/billing', icon: FileText, current: location.pathname.startsWith('/billing'), module: 'billing' },
  ];

  // Add admin-only modules
  if (canAccessModule('expenses')) {
    navigation.push({ 
      name: 'Expenses', 
      href: '/expenses', 
      icon: CreditCard, 
      current: location.pathname.startsWith('/expenses'),
      module: 'expenses'
    });
  }

  if (canAccessModule('investments')) {
    navigation.push({ 
      name: 'Investments', 
      href: '/investments', 
      icon: TrendingUp, 
      current: location.pathname.startsWith('/investments'),
      module: 'investments'
    });
  }

  if (canAccessModule('staff')) {
    navigation.push({ 
      name: 'Staff Management', 
      href: '/staff', 
      icon: Users, 
      current: location.pathname.startsWith('/staff'),
      module: 'staff'
    });
  }

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <SidebarContent navigation={navigation} handleLogout={handleLogout} currentUser={currentUser} userRole={userRole} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="sidebar-gradient border-r border-white/30">
            <SidebarContent navigation={navigation} handleLogout={handleLogout} currentUser={currentUser} userRole={userRole} />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top bar */}
        <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3">
          <button
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarContent({ navigation, handleLogout, currentUser, userRole }) {
  return (
    <div className="flex-1 flex flex-col min-h-0 h-full">
      <div className="flex-1 flex flex-col pt-6 pb-4 overflow-y-auto">
        <div className="flex items-center flex-shrink-0 px-6 mb-8">
          <h1 className="text-3xl font-bold gradient-text">Littlo</h1>
        </div>
        <nav className="flex-1 px-4 space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-item ${
                  item.current
                    ? 'nav-item-active'
                    : 'nav-item-inactive'
                }`}
              >
                <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="flex-shrink-0 p-4 border-t border-white/20">
        <div className="glass-effect p-4">
          <div className="flex items-center">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {currentUser?.email}
              </p>
              <p className="text-xs text-primary-600 font-medium capitalize">
                {userRole}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="ml-3 flex-shrink-0 p-2 text-gray-400 hover:text-primary-500 rounded-lg hover:bg-white/20 transition-all duration-200"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
